// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Contacts importer — admin page.
//
// Imports personal/professional contacts (e.g. your iPhone or Google
// Contacts) into the `contacts` table. This is DIFFERENT from the Clio
// Import page, which imports clients/matters/billing into the clients +
// billing tables. Contacts here are kept separate so a future
// conflict-checker can cross-reference them against clients.
//
// Flow per import:
//   1. Pick file (.vcf / .csv). Format auto-detects but the user can
//      override the format on the upload card.
//   2. Backend parses and returns a preview row list, each flagged with
//      `alreadyImported` (true if a row with the same content_hash already
//      exists in the contacts table). Already-imported rows are unchecked
//      by default.
//   3. User reviews, deselects anything they don't want, hits Import.
//   4. Backend inserts and reports inserted/skipped/errors.

import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../utils/adminApi';

const FORMAT_OPTIONS = [
  { value: 'auto',        label: 'Auto-detect',                hint: 'Detect from file contents' },
  { value: 'vcard',       label: 'vCard (.vcf)',               hint: 'Apple Contacts, Google vCard export' },
  { value: 'google_csv',  label: 'Google Contacts CSV',        hint: 'Google export "Google CSV"' },
  { value: 'outlook_csv', label: 'Outlook CSV',                hint: 'Microsoft Outlook export' },
];

const SOURCE_LABELS = {
  vcard_apple:   'Apple Contacts (vCard)',
  vcard_google:  'Google Contacts (vCard)',
  vcard_generic: 'vCard',
  google_csv:    'Google Contacts (CSV)',
  outlook_csv:   'Outlook (CSV)',
  manual:        'Manual',
};

const td = { padding: '8px 12px', fontSize: 13, fontFamily: 'Arial, sans-serif', borderBottom: '1px solid #eee', verticalAlign: 'top' };
const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };

function ContactPreviewTable({ rows, selected, onToggle, onToggleAll }) {
  if (!rows || !rows.length) return null;

  const allSelected = rows.every((_, i) => selected.has(i));
  const someSelected = !allSelected && rows.some((_, i) => selected.has(i));

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title">
        Preview ({rows.length.toLocaleString()} contacts){' '}
        <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>
          — {selected.size.toLocaleString()} selected
        </span>
      </div>
      {/* The horizontal scroll lives on a wrapper INSIDE the card, not on the
          card itself. Putting overflowX:'auto' on the card scrolls the title
          along with the table and clips content against the card's own
          padding; a dedicated wrap keeps the title fixed and gives the table
          its own scrollable strip. */}
      <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 36 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </th>
              <th style={th}>Name</th>
              <th style={th}>Company</th>
              <th style={th}>Email</th>
              <th style={th}>Phone</th>
              <th style={th}>Tags</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const primaryEmail = row.emails?.[0]?.value || '';
              const primaryPhone = row.phones?.[0]?.value || '';
              const isDup = row.alreadyImported;
              return (
                <tr key={i} style={{ background: isDup ? '#f9f9f9' : 'white', opacity: isDup ? 0.7 : 1 }}>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => onToggle(i)}
                    />
                  </td>
                  <td style={td}><strong>{row.display_name}</strong></td>
                  <td style={td}>{row.company || ''}</td>
                  {/* Email and Tags can be long; allow them to wrap rather
                      than forcing the table to expand horizontally. */}
                  <td style={{ ...td, wordBreak: 'break-word' }}>{primaryEmail}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{primaryPhone}</td>
                  <td style={{ ...td, wordBreak: 'break-word' }}>
                    {(row.tags || []).slice(0, 3).map(t => (
                      <span key={t} style={{ display: 'inline-block', padding: '2px 8px', background: '#eef0f4', borderRadius: 10, fontSize: 11, marginRight: 4, marginBottom: 2 }}>{t}</span>
                    ))}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {isDup
                      ? <span style={{ color: '#888', fontSize: 11 }}>Already imported</span>
                      : <span style={{ color: '#0F1838', fontSize: 11 }}>New</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FileUploadButton({ label, accept, onFile, disabled }) {
  const ref = useRef();
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files[0];
          if (file) {
            const text = await file.text();
            onFile(text, file.name);
          }
          e.target.value = '';
        }}
      />
      <button className="btn btn-primary" onClick={() => ref.current.click()} disabled={disabled}>
        {label}
      </button>
    </>
  );
}

export default function AdminContactsImportPage() {
  const [format, setFormat]       = useState('auto');
  const [filename, setFilename]   = useState('');
  const [preview, setPreview]     = useState(null); // { rows, source, total, newCount, duplicateCount }
  const [selected, setSelected]   = useState(new Set());
  const [loading, setLoading]     = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [results, setResults]     = useState(null); // { inserted, skipped, errors }
  const [recent, setRecent]       = useState(null); // { total, batches }

  // Load recent import summary on mount; refresh after a successful import.
  useEffect(() => { loadRecent(); }, []);
  function loadRecent() {
    adminApi.getContactsImportRecent()
      .then(setRecent)
      .catch(() => { /* non-fatal */ });
  }

  async function handleFile(text, name) {
    setLoading(true);
    setError('');
    setSuccess('');
    setPreview(null);
    setResults(null);
    setSelected(new Set());
    setFilename(name);
    try {
      const data = await adminApi.previewContactsImport(text, name, format);
      setPreview(data);
      // Default selection: everything that isn't already imported.
      const initial = new Set();
      data.rows.forEach((row, i) => { if (!row.alreadyImported) initial.add(i); });
      setSelected(initial);
      if (!data.rows.length) {
        setError('No contacts found in this file.');
      } else if (data.newCount === 0) {
        setSuccess(`Loaded ${data.total.toLocaleString()} contacts — all are already imported.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggle(i) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }
  function toggleAll(v) {
    if (!preview) return;
    setSelected(v ? new Set(preview.rows.map((_, i) => i)) : new Set());
  }

  async function handleImport() {
    if (!preview || !selected.size) return;
    setImporting(true);
    setError('');
    setSuccess('');
    setResults(null);
    try {
      const chosen = preview.rows.filter((_, i) => selected.has(i));
      const r = await adminApi.executeContactsImport(chosen);
      setResults(r);
      setSuccess(`Imported ${r.inserted.toLocaleString()} contacts.${r.skipped ? ` Skipped ${r.skipped.toLocaleString()} duplicate(s).` : ''}${r.errors.length ? ` ${r.errors.length} error(s).` : ''}`);
      // Drop the preview after a successful import so the page is ready
      // for the next file. The user can scroll down to the "Recent imports"
      // card to confirm what landed in the database.
      setPreview(null);
      setSelected(new Set());
      setFilename('');
      loadRecent();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  }

  function handleReset() {
    setPreview(null);
    setSelected(new Set());
    setError('');
    setSuccess('');
    setResults(null);
    setFilename('');
  }

  return (
    <div>
      <h1 className="page-title">Import Contacts</h1>
      <p className="page-subtitle">
        Import your personal or professional contacts from Google Contacts or Apple Contacts.
        Contacts are stored separately from clients and will be available to the conflict checker.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* ── Upload card ───────────────────────────────────────────────── */}
      {!preview && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">Upload a contacts file</div>
          <div className="card-subtitle">
            Supported formats: vCard (.vcf), Google Contacts CSV, Outlook CSV.
            Files up to ~25&nbsp;MB and several thousand contacts are fine.
          </div>

          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 6, fontFamily: 'Arial, sans-serif', fontWeight: 600 }}>
              File format
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {FORMAT_OPTIONS.map(opt => (
                <label key={opt.value}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6,
                    cursor: 'pointer', minWidth: 200,
                    background: format === opt.value ? '#0F1838' : 'white',
                    color: format === opt.value ? 'white' : '#222',
                  }}
                >
                  <input
                    type="radio"
                    checked={format === opt.value}
                    onChange={() => setFormat(opt.value)}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'Arial, sans-serif' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{opt.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="btn-row">
            <FileUploadButton
              label={loading ? 'Reading...' : 'Choose file'}
              accept=".vcf,.csv,text/vcard,text/csv"
              onFile={handleFile}
              disabled={loading}
            />
          </div>

          {/* Export instructions — collapsible help block */}
          <details style={{ marginTop: 20, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>How do I export my contacts?</summary>
            <div style={{ marginTop: 10, lineHeight: 1.6, color: '#444' }}>
              <p style={{ marginTop: 0 }}>
                <strong>Apple Contacts (Mac):</strong> Open Contacts → File → Select All → File → Export →
                Export vCard. Save as a <code>.vcf</code> file and upload it here.
              </p>
              <p>
                <strong>iPhone:</strong> The iPhone Contacts app doesn't export directly. Sign into
                <code> iCloud.com</code> from a computer, open Contacts, press <code>Cmd/Ctrl + A</code> to select
                all, click the gear → Export vCard.
              </p>
              <p>
                <strong>Google Contacts:</strong> Open <code>contacts.google.com</code> → Export (left sidebar) →
                select <em>Google CSV</em> for the richest data, or <em>vCard</em> for a portable format. Either works.
              </p>
              <p>
                <strong>Outlook:</strong> File → Open &amp; Export → Import/Export → Export to a file → Comma
                Separated Values → Contacts. Save the resulting CSV.
              </p>
              <p style={{ marginBottom: 0, color: '#888' }}>
                Re-uploading the same file later is safe — the importer detects already-imported rows
                by a content hash and skips them.
              </p>
            </div>
          </details>
        </div>
      )}

      {/* ── Preview + import ──────────────────────────────────────────── */}
      {preview && (
        <>
          <div className="card" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div className="card-title" style={{ marginBottom: 4 }}>
                  {filename || 'Uploaded file'}
                </div>
                <div className="card-subtitle" style={{ marginBottom: 0 }}>
                  Source: <strong>{SOURCE_LABELS[preview.source] || preview.source}</strong>{' '}
                  &middot; Total: <strong>{preview.total.toLocaleString()}</strong>{' '}
                  &middot; New: <strong>{preview.newCount.toLocaleString()}</strong>{' '}
                  &middot; Already imported: <strong>{preview.duplicateCount.toLocaleString()}</strong>
                </div>
              </div>
              <div className="btn-row" style={{ marginTop: 0 }}>
                <button className="btn" onClick={handleReset} disabled={importing}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={importing || !selected.size}
                >
                  {importing
                    ? 'Importing...'
                    : `Import ${selected.size.toLocaleString()} contact${selected.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>

          <ContactPreviewTable
            rows={preview.rows}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        </>
      )}

      {/* ── Recent imports ────────────────────────────────────────────── */}
      {!preview && recent && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">
            Recent imports{' '}
            <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>
              — {recent.total.toLocaleString()} contact{recent.total === 1 ? '' : 's'} total
            </span>
          </div>
          {!recent.batches.length ? (
            <div style={{ color: '#888', fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
              No contacts imported yet.
            </div>
          ) : (
            <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={th}>File</th>
                    <th style={th}>Source</th>
                    <th style={th}>Contacts</th>
                    <th style={th}>Imported</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.batches.map((b, i) => (
                    <tr key={i}>
                      <td style={{ ...td, wordBreak: 'break-word' }}>{b.source_file || <span style={{ color: '#999' }}>(no filename)</span>}</td>
                      <td style={td}>{SOURCE_LABELS[b.source] || b.source}</td>
                      <td style={td}>{b.count.toLocaleString()}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{b.imported_at ? new Date(b.imported_at).toLocaleString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Per-row import errors ─────────────────────────────────────── */}
      {results && results.errors && results.errors.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title" style={{ color: '#B85042' }}>
            {results.errors.length} row{results.errors.length === 1 ? '' : 's'} failed to import
          </div>
          <div className="table-wrap" style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {results.errors.map((e, i) => (
                  <tr key={i}>
                    <td style={td}>{e.name}</td>
                    <td style={{ ...td, wordBreak: 'break-word' }}><code style={{ fontSize: 12, color: '#B85042' }}>{e.error}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
